import { useState } from 'react'
import PropTypes from 'prop-types'
import Paper from 'components/Paper'
import IconButton from 'components/IconButton'
import Icon from 'components/Icon'
import PopoverNavItem from 'components/Popover/PopoverNavItem'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import { useUniqueID } from 'utils/hooks'

const TooltipMenu = ({
  icon,
  label,
  actions,
  errorActions,
  iconButtonProps,
  tooltipProps,
  hideWhenEmpty,
  clickElementProps,
  onOpen,
  onClose,
  ...otherProps
}) => {
  const [open, setOpen] = useState(false)
  const uniqueID = useUniqueID()
  const id = otherProps.id || uniqueID
  const allActions = actions.filter(o => !!o)
  const allErrorActions = errorActions.filter(o => !!o)

  const handleToggleOpen = () => {
    onOpen?.()
    setOpen(true)
  }

  if (hideWhenEmpty && !allActions.length && !allErrorActions.length) return hideWhenEmpty || null

  return (
    <ClickAwayListener
      onClickAway={() => {
        onClose?.()
        setOpen(false)
      }}
    >
      <div>
        <Tooltip
          id={id}
          open={open}
          arrow
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="bottom-end"
          title={
            <Paper variant="inverse" round="more">
              {!!allActions.length && (
                <Stack gap={0.5} p={0.5}>
                  {allActions.map((action, idx) => (
                    <PopoverNavItem
                      key={`${id}_action_${idx}`}
                      variant="inverse"
                      isLoading={action.isLoading}
                      onMouseUp={e => {
                        action.action?.(e)
                        onClose?.()
                        setOpen(false)
                      }}
                    >
                      {action.label}
                    </PopoverNavItem>
                  ))}
                </Stack>
              )}
              {!!allErrorActions.length && (
                <Stack
                  gap={0.5}
                  p={0.5}
                  bgcolor="background.error.main"
                  sx={({ globals }) => ({
                     ...(!allActions.length && {
                      borderTopLeftRadius: globals.borderRadius.more,
                      borderTopRightRadius: globals.borderRadius.more,
                    }),
                    borderBottomLeftRadius: globals.borderRadius.more,
                    borderBottomRightRadius: globals.borderRadius.more,
                  })}
                >
                  {allErrorActions.map((action, idx) => (
                    <PopoverNavItem
                      key={`${id}_errorAction_${idx}`}
                      variantHover="ghostInverse"
                      isLoading={action.isLoading}
                      onMouseUp={e => {
                        action.action?.(e)
                        onClose?.()
                        setOpen(false)
                      }}
                    >
                      {action.label}
                    </PopoverNavItem>
                  ))}
                </Stack>
              )}
            </Paper>
          }
          {...tooltipProps}
        >
          <div
            onClick={handleToggleOpen}
            {...clickElementProps}
          >
            {label && <span>{label}</span>}
            {!label && (
              <IconButton {...iconButtonProps}>
                <Icon icon={icon} />
              </IconButton>
            )}
          </div>
        </Tooltip>
      </div>
    </ClickAwayListener>
  )
}

TooltipMenu.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.node,
  actions: PropTypes.array,
  errorActions: PropTypes.array,
  iconButtonProps: PropTypes.object,
  tooltipProps: PropTypes.object,
  clickElementProps: PropTypes.object,
  hideWhenEmpty: PropTypes.any,
  id: PropTypes.string,
}

TooltipMenu.defaultProps = {
  icon: 'MoreVertical',
  actions: [],
  errorActions: [],
  iconButtonProps: {},
  clickElementProps: {},
  tooltipProps: {},
}

export default TooltipMenu
